import {Action, combineReducers, configureStore, ThunkAction,} from '@reduxjs/toolkit';
import userReducer from '../features/user/userSlice';
import menuReducer from '../features/menu/menuSlice';
import pageReducer from '../features/page/pageSlice';
import remoteDBCacheReducer from '../features/cache/remoteDBCacheSlice';
import dataTableReducer from '../features/datatable/dataTableSlice';
import errorToastReducer from '../features/error/errorToastSlice';
import templateReducer from '../features/template/templateSlice';
import notificationReducer from '../features/notification/notificationSlice';
import storage from "redux-persist/lib/storage/session";
import {createMigrate, persistReducer} from "redux-persist";
import {FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE} from "redux-persist/lib/constants";
import {migrateSchoolDefaultToSkote, migrateSkoteToLightDefault, migrateTemplateState} from "../features/template/templateSlice";

/**
 * Session-scoped persistence.
 *
 * This used to be `redux-persist/lib/storage` (localStorage), which left the
 * whole `user` slice on disk indefinitely — including PII (name, e-mail, phone,
 * birth date, photo path) and the menu/rights tree. The rehydrated user object
 * is load-bearing logic, not just display (ownership checks use
 * `userDetails.personDetails.person.id`, teacher screens scope by
 * `personDetails.teacherClasses[0].classeId`), so it cannot simply be dropped:
 * persisting it to sessionStorage keeps that logic working while tying the data
 * to the tab session. The session itself is an httpOnly cookie held by the
 * gateway and is never persisted here.
 *
 * Consequence: closing the browser, or opening a new tab, starts a fresh
 * session and asks the user to log in again.
 */
const persistConfig = {
    key: "root",
    // v2: template slice moved from { current } to { userOverride, schoolDefault }.
    // v3: Skote default sidebar switched from dark to light.
    // v4: Skote becomes the default (school-level) template.
    version: 4,
    storage,
    blacklist: ['cacheDB', 'page', 'dataTable', 'errorToast', 'notification'],
    migrate: createMigrate({
        2: migrateTemplateState,
        3: migrateSkoteToLightDefault,
        4: migrateSchoolDefaultToSkote,
    }, {debug: false}),
};

const reducer = combineReducers({
    user: userReducer,
    menu: menuReducer,
    page: pageReducer,
    cacheDB: remoteDBCacheReducer,
    dataTable: dataTableReducer,
    errorToast: errorToastReducer,
    template: templateReducer,
    notification: notificationReducer,
});

const persistedReducer = persistReducer(persistConfig, reducer);

export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        }),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;

